const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_KEY = process.env.REACT_APP_API_KEY;

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(API_KEY && { 'X-API-Key': API_KEY }),
});

// Generate or retrieve session ID from sessionStorage
export const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem('cartSessionId');
  if (!sessionId) {
    sessionId = 'cs_' + crypto.randomUUID();
    sessionStorage.setItem('cartSessionId', sessionId);
  }
  return sessionId;
};

// Get existing session ID (returns null if none exists)
export const getSessionId = () => {
  return sessionStorage.getItem('cartSessionId');
};

// Hash email for privacy (SHA-256)
const hashEmail = async (email) => {
  if (!email) return '';
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Create initial cart session when checkout starts
export const createCartSession = async (bookingData) => {
  // Generate a new session ID (don't store until success)
  const sessionId = 'cs_' + crypto.randomUUID();

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/cart-sessions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        sessionId,
        vehicleId: parseInt(bookingData?.vehicleId, 10) || 0,
        vehicleName: bookingData?.vanTitle || '',
        startDate: bookingData?.startDate || '',
        endDate: bookingData?.endDate || '',
        nights: parseInt(bookingData?.nights, 10) || 0,
        pickupLocation: bookingData?.pickupLocation?.name || '',
        dropoffLocation: bookingData?.dropoffLocation?.name || '',
      }),
    });

    if (!response.ok) {
      console.warn('Failed to create cart session:', response.status);
      return null;
    }

    // Only store session ID after successful creation
    sessionStorage.setItem('cartSessionId', sessionId);
    return await response.json();
  } catch (error) {
    // Non-blocking - don't interrupt user flow
    console.warn('Cart session creation failed:', error.message);
    return null;
  }
};

// Record step 1 completion (customer info)
export const recordStep1Completion = async (formData) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  try {
    const emailHash = await hashEmail(formData.email);

    const response = await fetch(`${API_BASE_URL}/api/v1/cart-sessions/${sessionId}/step/1`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        emailHash,
        country: formData.country || '',
        adults: parseInt(formData.adults) || 1,
        children: parseInt(formData.children) || 0,
        hasPets: formData.pets || false,
        numberOfPets: parseInt(formData.numberOfPets) || 0,
      }),
    });

    if (!response.ok) {
      console.warn('Failed to record step 1:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('Step 1 recording failed:', error.message);
    return null;
  }
};

// Record step 2 completion (insurance selection)
export const recordStep2Completion = async (step2Data) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/cart-sessions/${sessionId}/step/2`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        selectedInsurance: step2Data.selectedPlan || '',
      }),
    });

    if (!response.ok) {
      console.warn('Failed to record step 2:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('Step 2 recording failed:', error.message);
    return null;
  }
};

// Record step 3 completion (add-ons and total)
export const recordStep3Completion = async (step3Data, estimatedTotal) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  try {
    // Convert selectedAddOns object to the expected format
    const addonSelections = {};
    if (step3Data?.selectedAddOns) {
      Object.entries(step3Data.selectedAddOns).forEach(([id, selected]) => {
        if (selected) {
          const quantity = step3Data.quantities?.[id] || 1;
          addonSelections[id] = quantity;
        }
      });
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/cart-sessions/${sessionId}/step/3`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        addonSelections,
        estimatedTotal: estimatedTotal || 0,
      }),
    });

    if (!response.ok) {
      console.warn('Failed to record step 3:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('Step 3 recording failed:', error.message);
    return null;
  }
};
